import React, { useState } from "react";
import {
  StageTitle,
  AttendeesSearchBar,
  AttendeesListContainer,
  AttendeesList,
  AttendeeCard,
  AttendeesAvatar,
  AttendeesInfo,
  AttendeesInviteButton,
  InvitedList,
  AttendeesRemoveButton,
} from "./CreateEvent.styles";

export interface UserInviteListProps {
  title: string;
  attendees: Attendee[];
  searchTerm: string;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  isInvitedFn: (user: Attendee) => boolean;
  toggleInviteFn: (user: Attendee) => void;
  filterFn: (user: Attendee) => boolean;
  excludeIds?: number[];
}

const UserInviteList: React.FC<UserInviteListProps> = ({
  title,
  attendees,
  searchTerm,
  setSearchTerm,
  isInvitedFn,
  toggleInviteFn,
  filterFn,
  excludeIds = [],
}) => {
  const [avatarErrors, setAvatarErrors] = useState<{ [key: number]: boolean }>(
    {}
  );

  const handleAvatarError = (userId: number) => {
    setAvatarErrors((prev) => ({ ...prev, [userId]: true }));
  };

  const filteredAvailable = attendees.filter((att) => {
    const matchesSearch = att.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const passesFilter = filterFn(att);
    const notExcluded = !excludeIds.includes(att.id);
    const notInvited = !isInvitedFn(att);
    return matchesSearch && passesFilter && notExcluded && notInvited;
  });

  const invitedUsers = attendees.filter((att) => isInvitedFn(att));

  return (
    <div style={{ marginTop: 20 }}>
      <StageTitle>{title}</StageTitle>

      <AttendeesSearchBar
        type="text"
        placeholder={`Search ${title}...`}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      <AttendeesListContainer>
        <AttendeesList>
          <h3>Available</h3>
          {filteredAvailable.map((att) => (
            <AttendeeCard key={att.id}>
              <AttendeesAvatar>
                {!avatarErrors[att.id] && att.socialAvatarUrl ? (
                  <img
                    src={att.socialAvatarUrl}
                    alt={att.name}
                    onError={() => handleAvatarError(att.id)}
                  />
                ) : (
                  "Not Found"
                )}
              </AttendeesAvatar>
              <AttendeesInfo>
                <div className="name">{att.name}</div>
                <div className="type">
                  {att.myself_type || att.organization_type || "User"}
                </div>
              </AttendeesInfo>
              <AttendeesInviteButton
                onClick={() => toggleInviteFn(att)}
                className={isInvitedFn(att) ? "active" : ""}
              >
                +
              </AttendeesInviteButton>
            </AttendeeCard>
          ))}
        </AttendeesList>

        <InvitedList>
          <h3>Selected</h3>
          {invitedUsers.map((att) => (
            <AttendeeCard key={att.id}>
              <AttendeesAvatar>
                {!avatarErrors[att.id] && att.socialAvatarUrl ? (
                  <img
                    src={att.socialAvatarUrl}
                    alt={att.name}
                    onError={() => handleAvatarError(att.id)}
                  />
                ) : (
                  "Not Found"
                )}
              </AttendeesAvatar>
              <AttendeesInfo>
                <div className="name">{att.name}</div>
                <div className="type">
                  {att.myself_type || att.organization_type || "User"}
                </div>
              </AttendeesInfo>
              <AttendeesRemoveButton onClick={() => toggleInviteFn(att)}>
                ×
              </AttendeesRemoveButton>
            </AttendeeCard>
          ))}
        </InvitedList>
      </AttendeesListContainer>
    </div>
  );
};

export default UserInviteList;
